<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref} from "vue";
import ToggleNavigationButton from "./toggleNavigationButton.vue";
import PrimaryNavigationItemThemed from "./primaryNavigationItemThemed.vue";
import PrimaryNavigationItemThemedMobile from "./primaryNavigationItemThemedMobile.vue";
import ProfileBar from "./profileBar.vue";

/*
Header style: Logo left, navigation center
*/

const props = defineProps({
    logoUrl: {
        type: String,
        required: true
    },
    sidebarNavigation: {
        type: Object,
        required: true
    },
    userLoggedIn: {
        type: Boolean,
        required: false,
        default: false
    },
    accountCreationEnabled: {
        type: Boolean,
        required: false,
        default: false
    },
    theme: {
        type: String,
        required: false,
        default: 'default'
    },
    themeSettings: {
        type: Object,
        required: false,
        default: () => ({
            paddingTop: 0,
            paddingBottom: 0,
        })
    },
    preview: {
        type: Boolean,
        required: false,
        default: false
    }
});

const navigationOpen = ref(false);

const toggleNavigation = () => {
    navigationOpen.value = !navigationOpen.value;
};

const primaryNavigationContainerClasses = computed(() => {
    return 'primary-navigation-container primary-navigation-container-'+props.theme;
})

const primaryNavigationDesktopClasses = computed(() => {
    if (props.theme === 'theme-a') {
        return 'primary-navigation-theme-a'
    }
    if (props.theme === 'theme-b') {
        return 'primary-navigation-theme-b'
    }
    if (props.theme === 'theme-c') {
        return 'primary-navigation-theme-c'
    }
    if (props.theme === 'theme-d') {
        return 'primary-navigation-theme-d'
    }
})

const linkTarget = computed(() => {
    if (props.preview) {
        return '_blank';
    } else {
        return '_self';
    }
})

const profileBarContainerClasses = computed(() => {
    return 'profile-bar-container-'+props.theme;
})

const profileBarClasses = computed(() => {
    return 'profile-bar-'+props.theme;
})

const topLogoClasses = computed(() => {
    return 'top-logo top-logo-'+props.theme;
})

const profileBarLogoClasses = computed(() => {
    return 'profile-bar-logo profile-bar-logo-'+props.theme;
})

const toggleNavigationContainerClasses = computed(() => {
    return 'toggle-navigation-container-'+props.theme;
})

const showLoginButton = computed(() => {
    return props.themeSettings?.showLoginButton ? props.themeSettings.showLoginButton : false;
})

const navContainerBoxShadow = computed(() => {
    return props.themeSettings.dropShadow?.enabled ? '0 20px 25px -5px rgba(0,0,0,0.05), 0 8px 10px -6px rgba(0,0,0,0.05)' : 'none';
})

const headerPositionClass = computed(() => {
    if (!props.themeSettings.fixedHeader) {
        return '';
    }
    if (props.preview) {
        return props.themeSettings.fixedHeader === true ? 'absolute left-0 top-0 right-0 z-topbar' : '';
    } else {
        return props.themeSettings.fixedHeader === true ? 'fixed top-0 left-0 right-0 z-topbar' : '';
    }
})

const headerClass = computed(() => {
    if (!props.themeSettings.fixedHeader) {
        return 'top-navigation-static';
    }
    if (props.themeSettings.fixedHeader === true) {
        return 'top-navigation-floating';
    }
    return 'top-navigation-static';
})

const navContainerBackgroundColor = computed(() => {
    return props.themeSettings.navBarBgColor ? props.themeSettings.navBarBgColor : 'bg-white';
})

const navContainerTextColor = computed(() => {
    return props.themeSettings.navBarTextColor ? props.themeSettings.navBarTextColor : 'bg-white';
})

const navContainerPaddingTopMobile = computed(() => {
    return props.themeSettings.mobile.paddingTop ? `${props.themeSettings.mobile.paddingTop}px` : '14px';
});

const navContainerPaddingBottomMobile = computed(() => {
    return props.themeSettings.mobile.paddingBottom ? `${props.themeSettings.mobile.paddingBottom}px` : '14px';
});

const logoMaxWidthMobile = computed(() => {
    return props.themeSettings.mobile.logoWidth ? `${props.themeSettings.mobile.logoWidth}px` : '32px';
})

const listItemPaddingMobile = computed(() => {
    return props.themeSettings.mobile.navItemGap ? props.themeSettings.mobile.navItemGap+'px' : '12px';
})

const listItemFontSizeMobile = computed(() => {
    return props.themeSettings.mobile.navItemFontSize ? props.themeSettings.mobile.navItemFontSize+'px' : '15px';
})

const listSubItemFontSizeMobile = computed(() => {
    return props.themeSettings.mobile.navItemFontSize ? props.themeSettings.mobile.navItemFontSize * 0.8+'px' : '13px';
})

const navContainerPaddingTopDesktop = computed(() => {
    return props.themeSettings.desktop.paddingTop ? `${props.themeSettings.desktop.paddingTop}px` : '16px';
});

const navContainerPaddingBottomDesktop = computed(() => {
    return props.themeSettings.desktop.paddingBottom ? `${props.themeSettings.desktop.paddingBottom}px` : '16px';
});

const navContainerMaxWidth = computed(() => {
    if (props.themeSettings.fullWidth === true) {
        return '100%';
    }
    return props.themeSettings.maxWidth ? `${props.themeSettings.maxWidth}px` : '1280px';
});

const logoMaxWidthDesktop = computed(() => {
    return props.themeSettings.desktop.logoWidth ? `${props.themeSettings.desktop.logoWidth}px` : '96px';
})

const listItemPaddingRightDesktop = computed(() => {
    return props.themeSettings.desktop.navItemGap ? props.themeSettings.desktop.navItemGap+'px' : '20px';
})

const listItemFontSizeDesktop = computed(() => {
    return props.themeSettings.desktop.navItemFontSize ? props.themeSettings.desktop.navItemFontSize+'px' : '15px';
})

const listSubItemFontSizeDesktop = computed(() => {
    return props.themeSettings.desktop.navItemFontSize ? props.themeSettings.desktop.navItemFontSize*0.9+'px' : '15px';
})

const compensateForNavigationHeight = () => {
    const floatingNavigation = document.querySelector('.top-navigation-floating');

    if (!floatingNavigation) {
        return
    }

    const firstRow = document.querySelector('#primary-content .mfpbr-index-0');

    if (props.navigationStyle !== 'fullwidth') {
        // Measure the bar itself: #top-navigation also contains the mobile menu, which adds height while open
        const navHeight = floatingNavigation.querySelector('.theme-nav-container').offsetHeight;
        if (firstRow?.querySelector('.row-paddable')) {
            firstRow.querySelector('.row-paddable').style.paddingTop = `${navHeight}px`;
        } else {
            document.body.style.paddingTop = `${navHeight}px`;
        }
    }
};

onMounted(() => {
    nextTick(compensateForNavigationHeight);
    window.addEventListener('resize', compensateForNavigationHeight);
});

onBeforeUnmount(() => {
    window.removeEventListener('resize', compensateForNavigationHeight);
});

</script>

<template>
    <div id="top-navigation"
         class="z-topbar ccss-top-navigation-themed"
         :class="[preview ? 'container-queried' : 'media-queried', headerPositionClass, 'ccss-top-navigation-themed__'+theme,
         headerClass]"
    >
        <div class="relative px-4 theme-nav-container">
            <div class="relative flex items-center justify-between mx-auto md:h-full ">
                <div
                    class="w-full relative mx-auto"
                    :class="primaryNavigationContainerClasses"
                >
                    <div
                        :class="topLogoClasses"
                        class="shrink-0 md:mr-6 flex justify-center"
                    >
                        <a :target="linkTarget" :href="route('catchAll')" title="Gå till startsidan">
                            <img
                                class="object-contain object-left theme-logo"
                                :src="props.logoUrl"
                            />
                        </a>
                    </div>
                    <ul
                        class="ccss-primary-navigation ccss-primary-navigation-desktop primary-navigation-desktop w-full"
                        :class="primaryNavigationDesktopClasses"
                        id="primary-navigation"
                        ref="primary-navigation"
                    >
                        <li
                            v-for="(navigationItem, i) in sidebarNavigation.filter(ni => ni.visible === true)"
                            class="flex items-center justify-between md:justify-start relative"
                        >
                            <primary-navigation-item-themed
                                :item="navigationItem"
                                :preview="preview"
                            >
                                <a
                                    :target="linkTarget" :href="navigationItem.href"
                                    class="py-3 font-semibold whitespace-nowrap  hover:underline focus:underline focus:outline-none md:px-0 md:py-2 mf-link-immune"
                                >{{ navigationItem.name }}</a>
                            </primary-navigation-item-themed>
                        </li>
                    </ul>
                    <div
                        class="flex items-center space-x-2"
                        :class="profileBarContainerClasses"
                    >
                        <div class="profile-bar-container" :class="profileBarClasses">
                            <profile-bar
                                :account-creation-enabled="props.accountCreationEnabled"
                                :user-is-logged-in="props.userLoggedIn"
                                :show-login-button="showLoginButton"
                                :preview="props.preview"
                            ></profile-bar>
                        </div>

                        <div :class="profileBarLogoClasses">
                            <a :target="linkTarget" :href="route('catchAll')" title="Gå till startsidan">
                                <img
                                    class="object-contain object-left theme-logo"
                                    :src="props.logoUrl"
                                />
                            </a>
                        </div>

                        <div :class="toggleNavigationContainerClasses" class="flex items-center">
                            <toggle-navigation-button
                                @click="toggleNavigation"
                                class="toggle-navigation-button"
                                :class="navigationOpen ? 'active' : ''"
                            ></toggle-navigation-button>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <ul
            class="ccss-primary-navigation ccss-primary-navigation-mobile primary-navigation-mobile"
            :class="navigationOpen ? 'open' : ''"
            id="primary-navigation-mobile"
            ref="primary-navigation"
        >
            <li
                v-for="(navigationItem, i) in sidebarNavigation.filter(ni => ni.visible === true)"
                class="relative px-2"
                :style="`transition-delay: ${i * 100}ms;`"
            >
                <primary-navigation-item-themed-mobile
                    :item="navigationItem"
                >
                    <a
                        :target="linkTarget" :href="navigationItem.href"
                        class="font-semibold whitespace-nowrap hover:underline focus:underline focus:outline-none md:px-0 mf-link-immune"
                    >{{ navigationItem.name }}</a>
                </primary-navigation-item-themed-mobile>
            </li>
        </ul>
    </div>

</template>

<style scoped lang="scss">

#top-navigation {
    container-type: inline-size;
}

#top-navigation {
    box-shadow: v-bind(navContainerBoxShadow);
}

.primary-navigation-container {
    max-width: v-bind(navContainerMaxWidth);
}

.theme-nav-container {
    background-color: v-bind(navContainerBackgroundColor);
    color: v-bind(navContainerTextColor);
    padding-top: v-bind(navContainerPaddingTopMobile);
    padding-bottom: v-bind(navContainerPaddingBottomMobile);
}

:deep(.profile-bar-container button) {
    font-size: v-bind(listItemFontSizeDesktop);
    padding: 0.45em 0.55em !important;
}

:deep(.profile-bar-container .text-wlPrimary),
:deep(.profile-bar-container svg) {
    color: v-bind(navContainerTextColor);
    fill: v-bind(navContainerTextColor);
}

:deep(.profile-bar-container span),
:deep(.profile-bar-container ul li a) {
    font-size: v-bind(listItemFontSizeDesktop);
}

:deep(.profile-bar-container .bg-white) {
    background-color: v-bind(navContainerBackgroundColor);
}

.theme-logo {
    width: 100%;
    max-width: v-bind(logoMaxWidthMobile);
}

@mixin desktopMain {
    #primary-navigation {
        position: static;
        display: flex;
        flex-wrap: wrap;
        height: 100%;
        padding-top: 0;
        padding-bottom:0;
    }

    .theme-nav-container {
        padding-top: v-bind(navContainerPaddingTopDesktop);
        padding-bottom: v-bind(navContainerPaddingBottomDesktop);
    }

    .theme-logo {
        width: 100%;
        max-width: v-bind(logoMaxWidthDesktop);
    }

    .top-logo-theme-d {
        padding-bottom: 8px;
    }

    .primary-navigation-container-theme-a,
    .primary-navigation-container-theme-b,
    .primary-navigation-container-theme-c {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .primary-navigation-theme-a {
        display: flex;
        justify-content: flex-start;
    }
    .primary-navigation-theme-b {
        display: flex;
        justify-content: center;
    }
    .primary-navigation-theme-c {
        display: flex;
        justify-content: flex-end;
    }
    .primary-navigation-theme-d {
        display: flex;
        justify-content: center;
        margin-top: 1rem;
    }

    .profile-bar-theme-d {
        position: absolute;
        top: 1rem;
        right: 1rem;
    }

    .profile-bar-logo {
        display: none;
    }

    #primary-navigation li {
        padding-right: v-bind(listItemPaddingRightDesktop);
        font-size: v-bind(listItemFontSizeDesktop);
    }

    #primary-navigation.primary-navigation-theme-c li {
        padding-left: v-bind(listItemPaddingRightDesktop);
    }

    #primary-navigation.primary-navigation-theme-b li,
    #primary-navigation.primary-navigation-theme-d li {
         padding-right: calc(v-bind(listItemPaddingRightDesktop) / 2);
         padding-left: calc(v-bind(listItemPaddingRightDesktop) / 2);
    }

    :deep(#primary-navigation li ul) {
        background-color: v-bind(navContainerBackgroundColor);
        line-height: 1.3em;
    }

    :deep(#primary-navigation li ul li a) {
        font-size: v-bind(listSubItemFontSizeDesktop);
        line-height: 1.3em;
    }

    .toggle-navigation-button {
        display: none;
    }

    .primary-navigation-desktop {
        display: block;
    }

    .primary-navigation-mobile {
        display: none;
    }
}

@mixin desktopQueried {
    .theme-nav-container {
        padding-top: v-bind(navContainerPaddingTopDesktop);
        padding-bottom: v-bind(navContainerPaddingBottomDesktop);
    }

    .theme-logo {
        width: 100%;
        max-width: v-bind(logoMaxWidthDesktop);
    }

    #primary-navigation li {
        padding-right: v-bind(listItemPaddingRightDesktop);
        font-size: v-bind(listItemFontSizeDesktop);
    }

    .toggle-navigation-button {
        display: none;
    }
}

@mixin mobileMain {
    .primary-navigation-container-theme-a,
    .primary-navigation-container-theme-b,
    .primary-navigation-container-theme-c {
        display: flex;
        align-items: center;
        justify-content: space-between;
    }

    .profile-bar-container-theme-d {
        display: flex;
        justify-content: space-between;
        align-items: center;
    }

    :deep(.profile-bar-container button) {
        font-size: v-bind(listItemFontSizeMobile);
        padding: 0.45em 0.55em !important;
    }

    :deep(.profile-bar-container .text-wlPrimary),
    :deep(.profile-bar-container svg) {
        color: v-bind(navContainerTextColor);
        fill: v-bind(navContainerTextColor);
    }

    :deep(.profile-bar-container span),
    :deep(.profile-bar-container ul li a) {
        font-size: v-bind(listItemFontSizeMobile);
    }

    :deep(.toggle-navigation-button span) {
        background-color: v-bind(navContainerTextColor);
    }
    :deep(.toggle-navigation-button svg) {
        fill: v-bind(navContainerTextColor);
    }

    .profile-bar-theme-d {
    }

    .toggle-navigation-container-theme-d {
    }

    .top-logo-theme-d {
        display: none;
    }

    .profile-bar-logo {
        display: none;
    }

    .profile-bar-logo-theme-d {
        display: block;
    }
}

@mixin mobileQueried {
    #primary-navigation-mobile.open {
        opacity: 1;
        max-height: 100vh;
        padding-top: 16px;
        padding-bottom: 16px;
    }

    #primary-navigation-mobile li {
        overflow: hidden;
        max-height: 0;
        transform: translateY(-1rem);
        transition: all 0.25s;
        opacity: 0;
    }

    #primary-navigation-mobile li a {
        font-size: v-bind(listItemFontSizeMobile);
        line-height: 1.3em;
    }

    #primary-navigation-mobile.open li {
        max-height: 300px;
        transform: translateY(0rem);
        opacity: 1;
        padding-top: v-bind(listItemPaddingMobile);
        padding-bottom: v-bind(listItemPaddingMobile);
    }

    .primary-navigation-desktop {
        display: none;
    }

    .primary-navigation-mobile {
        display: block;
    }

    #primary-navigation-mobile {
        transform: translateY(0rem);
        transition: all 0.25s;
        max-height: 0;
        overflow-y: hidden;
        background-color: v-bind(navContainerBackgroundColor);
        color: v-bind(navContainerTextColor);
    }
}

/** Container queries, desktop **/
@container (width > 767px) {
    .container-queried {
        @include desktopMain;
        @include desktopQueried;
    }
}

/** Container queries, mobile **/
@container (width < 768px) {
    .container-queried {
        @include mobileMain;
        @include mobileQueried;
    }

    :deep(.container-queried #primary-navigation-mobile li ul li a) {
        font-size: v-bind(listSubItemFontSizeMobile);
        line-height: 1.3em;
    }
}

/** Media queries, desktop **/
@media screen and (min-width: 768px) {
    .media-queried {
        @include desktopMain;
        @include desktopQueried;
    }
}

/** Media queries, mobile **/
@media screen and (max-width: 767px) {
    @include mobileMain;

    .media-queried {
        @include mobileQueried;
    }

    .media-queried :deep(#primary-navigation-mobile li ul li a) {
        font-size: v-bind(listSubItemFontSizeMobile);
        line-height: 1.3em;
    }
}
</style>
